"use client"

import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "./lib/utils"

interface DatePickerDialogProps {
  value?: Date
  onChange?: (date: Date) => void
  onClose?: () => void
}

type ViewMode = "date" | "month" | "year"

const MONTH_NAMES_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]
const MONTH_NAMES_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/** Tries to parse a user-typed string into a Date.
 *  Accepts: MM/DD/YYYY, DD-MM-YYYY, YYYY-MM-DD, "Jan 15 2025", "15 January 2025", and
 *  anything JS's Date constructor can handle. Returns null on failure.
 */
function parseTypedDate(raw: string): Date | null {
  const str = raw.trim()
  if (!str) return null

  // MM/DD/YYYY  or  DD-MM-YYYY  or  DD.MM.YYYY
  const numeric = str.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/)
  if (numeric) {
    const [, a, b, y] = numeric
    // Try MM/DD/YYYY first, fall back to DD/MM/YYYY
    const attempt1 = new Date(+y, +a - 1, +b)
    if (!isNaN(attempt1.getTime()) && attempt1.getFullYear() === +y) return attempt1
    const attempt2 = new Date(+y, +b - 1, +a)
    if (!isNaN(attempt2.getTime()) && attempt2.getFullYear() === +y) return attempt2
    return null
  }

  // YYYY-MM-DD
  const iso = str.match(/^(\d{4})[\/\-\.](\d{1,2})[\/\-\.](\d{1,2})$/)
  if (iso) {
    const d = new Date(+iso[1], +iso[2] - 1, +iso[3])
    return isNaN(d.getTime()) ? null : d
  }

  // "Jan 15 2025" or "January 15, 2025"
  const namedMonth = str.match(/^([A-Za-z]+)\s+(\d{1,2})\s*,?\s*(\d{4})$/)
  if (namedMonth) {
    const mo = findMonth(namedMonth[1])
    if (mo !== -1) {
      const d = new Date(+namedMonth[3], mo, +namedMonth[2])
      return isNaN(d.getTime()) ? null : d
    }
  }

  // "15 Jan 2025" or "15 January 2025"
  const dayFirst = str.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/)
  if (dayFirst) {
    const mo = findMonth(dayFirst[2])
    if (mo !== -1) {
      const d = new Date(+dayFirst[3], mo, +dayFirst[1])
      return isNaN(d.getTime()) ? null : d
    }
  }

  // Last resort: JS Date (handles "2025-06-15", long-form English dates, etc.)
  const fallback = new Date(str)
  return isNaN(fallback.getTime()) ? null : fallback
}

function findMonth(name: string): number {
  const lower = name.toLowerCase()
  let idx = MONTH_NAMES_FULL.findIndex((m) => m.toLowerCase().startsWith(lower))
  if (idx === -1) idx = MONTH_NAMES_SHORT.findIndex((m) => m.toLowerCase().startsWith(lower))
  return idx
}

function formatDate(d: Date): string {
  return (
    String(d.getMonth() + 1).padStart(2, "0") +
    "/" +
    String(d.getDate()).padStart(2, "0") +
    "/" +
    d.getFullYear()
  )
}

export function DatePickerDialog({ value, onChange, onClose }: DatePickerDialogProps) {
  const [viewMode, setViewMode] = React.useState<ViewMode>("date")
  const today = new Date()
  const [displayMonth, setDisplayMonth] = React.useState(value?.getMonth() ?? today.getMonth())
  const [displayYear, setDisplayYear] = React.useState(value?.getFullYear() ?? today.getFullYear())

  // Text input state – tracks what the user has typed
  const [inputText, setInputText] = React.useState(value ? formatDate(value) : "")
  const [inputInvalid, setInputInvalid] = React.useState(false)

  // Keep the input in sync when `value` changes externally
  React.useEffect(() => {
    if (value) {
      setInputText(formatDate(value))
      setInputInvalid(false)
    }
  }, [value])

  const dayNames = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"]

  const getDaysInMonth = (month: number, year: number) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfMonth = (month: number, year: number) => new Date(year, month, 1).getDay()

  // ── Text input handlers ────────────────────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    setInputText(raw)

    if (!raw) {
      setInputInvalid(false)
      return
    }

    const parsed = parseTypedDate(raw)
    if (parsed) {
      setInputInvalid(false)
      setDisplayMonth(parsed.getMonth())
      setDisplayYear(parsed.getFullYear())
      onChange?.(parsed)
    } else {
      setInputInvalid(true)
    }
  }

  /** On blur, normalise a valid date to MM/DD/YYYY; leave invalid text as-is. */
  const handleInputBlur = () => {
    if (!inputText) return
    const parsed = parseTypedDate(inputText)
    if (parsed) {
      setInputText(formatDate(parsed))
      setInputInvalid(false)
    }
  }

  // ── Calendar click handlers ────────────────────────────────────────────────

  const handleDateClick = (day: number) => {
    const newDate = new Date(displayYear, displayMonth, day)
    setInputText(formatDate(newDate))
    setInputInvalid(false)
    onChange?.(newDate)
    onClose?.()
  }

  const handleMonthClick = (monthIndex: number) => {
    setDisplayMonth(monthIndex)
    setViewMode("date")
  }

  const handleYearClick = (year: number) => {
    setDisplayYear(year)
    setViewMode("month")
  }

  const handleTodayClick = () => {
    const todayDate = new Date()
    setDisplayMonth(todayDate.getMonth())
    setDisplayYear(todayDate.getFullYear())
    setInputText(formatDate(todayDate))
    setInputInvalid(false)
    onChange?.(todayDate)
    onClose?.()
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  const handlePrevious = () => {
    if (viewMode === "date") {
      if (displayMonth === 0) { setDisplayMonth(11); setDisplayYear(displayYear - 1) }
      else setDisplayMonth(displayMonth - 1)
    } else if (viewMode === "month") {
      setDisplayYear(displayYear - 1)
    } else {
      setDisplayYear(displayYear - 10)
    }
  }

  const handleNext = () => {
    if (viewMode === "date") {
      if (displayMonth === 11) { setDisplayMonth(0); setDisplayYear(displayYear + 1) }
      else setDisplayMonth(displayMonth + 1)
    } else if (viewMode === "month") {
      setDisplayYear(displayYear + 1)
    } else {
      setDisplayYear(displayYear + 10)
    }
  }

  // ── Render views ───────────────────────────────────────────────────────────

  const renderDateView = () => {
    const daysInMonth = getDaysInMonth(displayMonth, displayYear)
    const firstDay = getFirstDayOfMonth(displayMonth, displayYear)
    const prevMonthDays =
      displayMonth === 0 ? getDaysInMonth(11, displayYear - 1) : getDaysInMonth(displayMonth - 1, displayYear)

    const days: React.ReactNode[] = []

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(
        <button key={`prev-${i}`} className="h-9 w-9 text-center text-sm text-light-text-secondary dark:text-dark-text-secondary opacity-40" disabled>
          {prevMonthDays - i}
        </button>,
      )
    }

    const todayDate = new Date()
    const isCurrentMonth = displayMonth === todayDate.getMonth() && displayYear === todayDate.getFullYear()

    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = isCurrentMonth && day === todayDate.getDate()
      const isSelected =
        value && day === value.getDate() && displayMonth === value.getMonth() && displayYear === value.getFullYear()

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={cn(
            "h-9 w-9 text-center text-sm rounded-md transition-colors",
            "hover:bg-primary/10 hover:text-primary",
            "text-light-text-primary dark:text-dark-text-primary",
            isToday && "font-semibold text-primary",
            isSelected && "bg-primary text-white hover:bg-primary hover:text-white",
          )}
        >
          {day}
        </button>,
      )
    }

    const remainingDays = 42 - days.length
    for (let i = 1; i <= remainingDays; i++) {
      days.push(
        <button key={`next-${i}`} className="h-9 w-9 text-center text-sm text-light-text-secondary dark:text-dark-text-secondary opacity-40" disabled>
          {i}
        </button>,
      )
    }

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-7 gap-1">
          {dayNames.map((day) => (
            <div key={day} className="h-9 w-9 text-center text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary flex items-center justify-center">
              {day}
            </div>
          ))}
          {days}
        </div>
      </div>
    )
  }

  const renderMonthView = () => {
    const todayDate = new Date()
    const isCurrentYear = displayYear === todayDate.getFullYear()

    return (
      <div className="grid grid-cols-4 gap-4 py-4">
        {MONTH_NAMES_SHORT.map((month, index) => {
          const isCurrentMonth = isCurrentYear && index === todayDate.getMonth()
          const isSelected = value && index === value.getMonth() && displayYear === value.getFullYear()

          return (
            <button
              key={month}
              onClick={() => handleMonthClick(index)}
              className={cn(
                "h-12 rounded-md text-sm font-medium transition-colors",
                "hover:bg-primary/10 hover:text-primary",
                "text-light-text-primary dark:text-dark-text-primary",
                isCurrentMonth && "text-primary font-semibold",
                isSelected && "bg-primary text-white hover:bg-primary hover:text-white",
              )}
            >
              {month}
            </button>
          )
        })}
      </div>
    )
  }

  const renderYearView = () => {
    const startYear = Math.floor(displayYear / 10) * 10
    const todayDate = new Date()
    const currentYear = todayDate.getFullYear()

    return (
      <div className="grid grid-cols-4 gap-4 py-4">
        {Array.from({ length: 10 }, (_, i) => startYear + i).map((year) => {
          const isCurrentYear = year === currentYear
          const isSelected = value && year === value.getFullYear()

          return (
            <button
              key={year}
              onClick={() => handleYearClick(year)}
              className={cn(
                "h-12 rounded-md text-sm font-medium transition-colors",
                "hover:bg-primary/10 hover:text-primary",
                "text-light-text-primary dark:text-dark-text-primary",
                isCurrentYear && "text-primary font-semibold",
                isSelected && "bg-primary text-white hover:bg-primary hover:text-white",
              )}
            >
              {year}
            </button>
          )
        })}
      </div>
    )
  }

  const getHeaderText = () => {
    if (viewMode === "date") return `${MONTH_NAMES_FULL[displayMonth]}, ${displayYear}`
    if (viewMode === "month") return String(displayYear)
    const startYear = Math.floor(displayYear / 10) * 10
    return `${startYear} - ${startYear + 9}`
  }

  return (
    <div className="w-auto p-4 bg-light-card-bg dark:bg-dark-card-bg border border-gray-200 dark:border-[rgba(255,255,255,0.04)] rounded-lg shadow-lg">
      <div className="space-y-4">

        {/* ── Text input ─────────────────────────────────────────────────── */}
        <div>
          <input
            type="text"
            value={inputText}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            placeholder="MM/DD/YYYY or Jan 15 2025…"
            className={cn(
              "w-full px-3 py-1.5 text-sm rounded-md border transition-colors",
              "bg-transparent text-light-text-primary dark:text-dark-text-primary",
              "placeholder:text-light-text-secondary dark:placeholder:text-dark-text-secondary",
              "focus:outline-none focus:ring-2 focus:ring-primary/30",
              inputInvalid
                ? "border-red-400 dark:border-red-500"
                : "border-gray-200 dark:border-[rgba(255,255,255,0.08)]",
            )}
          />
          {inputInvalid && (
            <p className="mt-1 text-xs text-red-500">
              Try MM/DD/YYYY, Jan 15 2025, or 15 January 2025
            </p>
          )}
        </div>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between">
          <button
            onClick={handlePrevious}
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-light-table-header dark:hover:bg-dark-table-header transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-light-text-primary dark:text-dark-text-primary" />
          </button>

          <button
            onClick={() => {
              if (viewMode === "date") setViewMode("month")
              else if (viewMode === "month") setViewMode("year")
            }}
            className={cn(
              "text-sm font-medium px-3 py-1 rounded-md transition-colors",
              "text-light-text-primary dark:text-dark-text-primary",
              viewMode !== "year" && "hover:bg-primary/10 hover:text-primary",
            )}
          >
            {getHeaderText()}
          </button>

          <button
            onClick={handleNext}
            className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-light-table-header dark:hover:bg-dark-table-header transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-light-text-primary dark:text-dark-text-primary" />
          </button>
        </div>

        {/* ── Calendar body ───────────────────────────────────────────────── */}
        {viewMode === "date" && renderDateView()}
        {viewMode === "month" && renderMonthView()}
        {viewMode === "year" && renderYearView()}

        {/* ── Today shortcut ──────────────────────────────────────────────── */}
        <div className="flex justify-center pt-2 border-t border-gray-200 dark:border-[rgba(255,255,255,0.04)]">
          <button onClick={handleTodayClick} className="text-sm font-medium text-primary hover:underline transition-colors">
            Today
          </button>
        </div>
      </div>
    </div>
  )
}